import { useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getCreativeTrendRelatedCreatives,
  getUgcTrendRelatedVideos,
} from '@opm/services';
import { useStableValue } from '@opm/utils/stableKey';
import { toVideoCardItem } from '@opm/utils/videoAdapter';
import { ICreativeItem } from '../types';
import { DRAWER_RELATED_CREATIVES_COUNT } from '../constants';

/**
 * Drawer 内 Related Creatives 数据 Hook（独立调 /related_creatives 接口）
 *
 * 设计：
 * - 单次拉取 page=1, page_size=8（不分页、不滚动加载）
 * - sort 默认 `view_count desc`（与 /materials/list 一致）
 * - 主键 (game_id, topic) 缺失时不发起请求
 *
 * 接口文档：specs/CreativeTrends/api/related_creatives.md
 */
export function useDrawerRelatedCreatives({
  filter,
  sortItem,
  sortModel = 'desc',
}: {
  /** 上层 filter（含 game_id、topic、start_time、end_time、channels 等） */
  filter: any;
  sortItem?: string;
  sortModel?: 'asc' | 'desc';
}) {
  const [list, setList] = useState<ICreativeItem[]>([]);
  const [total, setTotal] = useState(0);

  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setList([]);
      setTotal(0);
    },
  });

  const stableFilter = useStableValue(filter);

  const params = useMemo(() => {
    if (!stableFilter?.game_id || !stableFilter?.topic) return null;
    return {
      ...stableFilter,
      page: 1,
      page_size: DRAWER_RELATED_CREATIVES_COUNT,
      sort_item: sortItem || 'view_count',
      sort_model: sortModel,
    };
  }, [stableFilter, sortItem, sortModel]);

  useEffect(() => {
    if (!params) return;
    const requestFunc =
      params?.source_type === 'ugc'
        ? getUgcTrendRelatedVideos
        : getCreativeTrendRelatedCreatives;
    wrapperCancelRequest(requestFunc, params)
      .then((response: any) => {
        const rawList = response?.list || [];
        // UGC 分支：在 hook 层把 VideoItem 适配成 VideoCard 形状，组件直接消费
        // CreativeTrends 分支：保持原 ICreativeItem 不变
        const adapted =
          params?.source_type === 'ugc'
            ? rawList.map(toVideoCardItem)
            : rawList;
        setList(adapted);
        setTotal(response?.total || 0);
      })
      .catch(() => {});
  }, [params]);

  return { list, total, loading };
}
